import {Box, HStack, Heading, Image, SimpleGrid} from '@chakra-ui/react';
import {ChevronLeftIcon} from '@chakra-ui/icons';
import {useNavigate, useParams} from 'react-router-dom';
import {HaliRenderOne, useHalCollection} from '@synako/haligator';
import {useClient} from '@/haligator-factory';
import {State} from '@synako/halitrek';
import {ItemCatalogue2Desc} from 'api-types/ffconnect-hal';
import {
  imageOptimiseeH,
  placeholderImage,
} from '@/domains/mediatheque/optimisation';
import {useTranslation} from 'react-i18next';
import type {CatalogueMarketplace} from 'api-types/ffconnect';
import {isItemCatalogueMarketplace, routes} from 'ff-common';
import {useClientRoute} from '@/hooks/auth/useClientRoute';
import {CatalogMarketplaceBreadcrumb} from '@/domains/catalogue/components/CatalogMarketplaceBreadcrumb';
import {ProduitCaracteristiques} from './ProduitCaracteristiques';
import {ProduitMarketplaceResume} from './ProduitMarketplaceResume';
import {useMarketplaceLigneDePanier} from '@/hooks/panier/useMarketplaceLigneDePanier';
import {Spinner} from '@chakra-ui/react';

export const ProduitMarketplaceView: React.FC = () => {
  const {marketplace_id, ref_tiers} = useParams<{
    marketplace_id: CatalogueMarketplace;
    ref_tiers: string;
  }>();
  const client = useClient();

  const marketplacesData = useHalCollection(client.follow('marketplaces'));
  const isValidMarketplaceId = marketplacesData.items?.some(
    (item) => item.data.marketplace_id === marketplace_id,
  );

  if (!marketplace_id || !ref_tiers) {
    return null;
  }

  if (marketplacesData.loading) {
    return <Spinner size='lg' />;
  }

  if (!isValidMarketplaceId) {
    return null;
  }

  return (
    <HaliRenderOne
      nav={client.follow('go:item_catalogue_2', {
        marketplace_id,
        code: ref_tiers,
      })}
      render={({state}) => (
        <Box
          bg='white.100'
          p={{base: '', md: '30px 30px 30px 40px'}}
        >
          <ProductMarketplaceItem itemState={state} />
        </Box>
      )}
    />
  );
};

const ProductMarketplaceItem: React.FC<{
  itemState: State<ItemCatalogue2Desc>;
}> = ({itemState}) => {
  const navigate = useNavigate();
  const {t} = useTranslation();
  const {buildClientRoute} = useClientRoute();

  const data = itemState.data;
  const marketplaceLigneDePanier = useMarketplaceLigneDePanier(
    data.marketplace_id,
    data.code,
  );

  if (!isItemCatalogueMarketplace(data)) {
    return null;
  }
  const prixHT = data.prix_unitaire_ht;
  const tva = data.tva;
  const marketplaceId = data.marketplace_id;

  const imageUrl = data.image_principale_url;
  const crumbs = [
    {name: t('global.accueil'), link: buildClientRoute(routes.index)},
    {
      name: t(`marketplace.${marketplaceId}`),
      link: buildClientRoute(
        routes.client.catalogue,
        {},
        {marketplace_id: marketplaceId},
      ),
    },
    {name: data.libelle},
  ];

  return (
    <>
      <HStack
        gap='0'
        display={{base: 'none', md: 'flex'}}
      >
        <CatalogMarketplaceBreadcrumb
          crumbs={crumbs}
          textColor='gray.dark'
          headingTextColor='gray.dark'
        />
      </HStack>
      <HStack
        p='20px'
        display={{base: 'flex', md: 'none'}}
        onClick={() => {
          navigate(-1);
        }}
        cursor='pointer'
      >
        <ChevronLeftIcon
          boxSize='22px'
          color='gray.dark'
        />
        <Heading
          as='h5'
          fontSize='sm'
          fontWeight='400'
          color='gray.dark'
        >
          {t('global.retour')}
        </Heading>
      </HStack>
      <SimpleGrid
        columns={{base: 1, md: 2}}
        spacing={6}
      >
        <Box>
          <Box
            position='relative'
            p={{base: '10px 20px 0px 20px', md: ''}}
          >
            <Box
              m={{base: '10px', md: '20px'}}
              display={{base: 'block', md: 'block'}}
            >
              <HStack
                height={{base: '274px', md: '372px'}}
                width='100%'
                justifyContent='center'
              >
                <Image
                  src={
                    imageUrl ? imageOptimiseeH(imageUrl, 372) : placeholderImage
                  }
                  alt={data.libelle}
                  objectFit='contain'
                  maxH={{base: '274px', md: '372px'}}
                />
              </HStack>
            </Box>
          </Box>
        </Box>
        <ProduitMarketplaceResume
          itemState={itemState}
          prixHT={prixHT}
          tva={tva}
          ligneDePanier={marketplaceLigneDePanier}
          marketplaceLigneDePanier={marketplaceLigneDePanier}
        />
        <ProduitCaracteristiques
          ean={data.ean}
          longueur_utile_mm={data.longueur_utile_mm}
          largeur_mm={data.largeur_mm}
          hauteur_mm={data.hauteur_mm}
          poids_net_kg={data.poids_net_kg}
        />
      </SimpleGrid>
    </>
  );
};
